import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  isBefore,
  isSameMonth,
  parse,
  startOfMonth,
  startOfWeek,
  endOfWeek,
} from 'date-fns';
import { tr } from 'date-fns/locale';
import { api } from '../api/client';

const USER_COLORS = [
  'bg-teal-400',
  'bg-sky-400',
  'bg-violet-400',
  'bg-amber-400',
  'bg-rose-400',
  'bg-lime-500',
  'bg-indigo-400',
];

function colorForUserId(id) {
  return USER_COLORS[Math.abs(id) % USER_COLORS.length];
}

function overlapsLeave(leave, task) {
  if (task.status !== 'in_progress') return false;
  const ls = parse(leave.start_date, 'yyyy-MM-dd', new Date());
  const deadline = task.deadline ? parse(task.deadline, 'yyyy-MM-dd', new Date()) : null;
  if (!deadline) return true;
  return !isBefore(deadline, ls);
}

export default function LeavePage() {
  const [tab, setTab] = useState('mine');
  const [leaves, setLeaves] = useState([]);
  const [calendarRows, setCalendarRows] = useState([]);
  const [monthCursor, setMonthCursor] = useState(() => new Date());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ start_date: '', end_date: '', note: '' });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [lastAddedLeave, setLastAddedLeave] = useState(null);
  const [overlapTasks, setOverlapTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [delegateTask, setDelegateTask] = useState(null);
  const [delegateUserId, setDelegateUserId] = useState('');
  const [delegateBusy, setDelegateBusy] = useState(false);

  const loadLeaves = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.getLeaves();
      setLeaves(Array.isArray(data) ? data : data.results || []);
    } catch {
      setError('İzinler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCalendar = useCallback(async () => {
    try {
      const { data } = await api.getTeamCalendar();
      const list = Array.isArray(data) ? data : data.results || [];
      setCalendarRows(list);
    } catch {
      setError('Takvim yüklenemedi.');
    }
  }, []);

  useEffect(() => {
    if (tab === 'mine') loadLeaves();
    else loadCalendar();
  }, [tab, loadLeaves, loadCalendar]);

  useEffect(() => {
    api.getUsers().then(({ data }) => {
      const arr = Array.isArray(data) ? data : data.results || [];
      setUsers(arr);
    }).catch(() => {});
  }, []);

  const monthDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(monthCursor), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(monthCursor), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [monthCursor]);

  const leavesByDay = useMemo(() => {
    const map = new Map();
    for (const row of calendarRows) {
      const a = parse(row.start_date, 'yyyy-MM-dd', new Date());
      const b = parse(row.end_date, 'yyyy-MM-dd', new Date());
      for (const d of eachDayOfInterval({ start: a, end: b })) {
        const key = format(d, 'yyyy-MM-dd');
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(row);
      }
    }
    return map;
  }, [calendarRows]);

  const submitLeave = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.start_date || !form.end_date) {
      setFormError('Başlangıç ve bitiş tarihleri zorunludur.');
      return;
    }
    if (parse(form.start_date, 'yyyy-MM-dd', new Date()) > parse(form.end_date, 'yyyy-MM-dd', new Date())) {
      setFormError('Bitiş tarihi başlangıçtan önce olamaz.');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.createLeave({
        start_date: form.start_date,
        end_date: form.end_date,
        note: form.note,
      });
      setForm({ start_date: '', end_date: '', note: '' });
      await loadLeaves();
      setLastAddedLeave(data);
      const { data: tasksData } = await api.getTasks({ tab: 'mine' });
      const mine = Array.isArray(tasksData) ? tasksData : tasksData.results || [];
      const hit = mine.filter((t) => overlapsLeave(data, t));
      setOverlapTasks(hit);
    } catch {
      setFormError('İzin kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const removeLeave = async (id) => {
    if (!window.confirm('Bu izin kaydını silmek istiyor musunuz?')) return;
    try {
      await api.deleteLeave(id);
      await loadLeaves();
      setLastAddedLeave(null);
      setOverlapTasks([]);
    } catch {
      setError('Silinemedi.');
    }
  };

  const submitDelegate = async () => {
    if (!delegateTask || !delegateUserId) return;
    setDelegateBusy(true);
    try {
      await api.delegateTask(delegateTask.id, { delegate_to: Number(delegateUserId) });
      setDelegateTask(null);
      setDelegateUserId('');
      const { data: tasksData } = await api.getTasks({ tab: 'mine' });
      const mine = Array.isArray(tasksData) ? tasksData : tasksData.results || [];
      if (lastAddedLeave) {
        setOverlapTasks(mine.filter((t) => overlapsLeave(lastAddedLeave, t)));
      }
    } catch {
      setError('Delegasyon başarısız.');
    } finally {
      setDelegateBusy(false);
    }
  };

  return (
    <div className="px-4 py-3 pb-24">
      <div className="flex rounded-xl bg-white border border-gray-100 p-1 mb-4">
        <button
          type="button"
          className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
            tab === 'mine' ? 'bg-brand-500 text-white' : 'text-gray-600'
          }`}
          onClick={() => setTab('mine')}
        >
          Kendi İznim
        </button>
        <button
          type="button"
          className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
            tab === 'team' ? 'bg-brand-500 text-white' : 'text-gray-600'
          }`}
          onClick={() => setTab('team')}
        >
          Ekip Takvimi
        </button>
      </div>

      {error && (
        <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl p-3">{error}</div>
      )}

      {tab === 'mine' && (
        <>
          {loading && (
            <div className="flex justify-center py-8">
              <div className="w-7 h-7 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {!loading && (
            <div className="space-y-3">
              {/* Delegasyon gereken işler — önce göster */}
              {lastAddedLeave && overlapTasks.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3">
                  <h3 className="text-sm font-semibold text-amber-800 mb-1">
                    ⚠️ Bu izin döneminde açık işleriniz
                  </h3>
                  <p className="text-xs text-amber-700 mb-2">
                    Sorumluluğu bir meslektaşınıza devredin.
                  </p>
                  <ul className="space-y-2">
                    {overlapTasks.map((t) => (
                      <li key={t.id} className="bg-white rounded-xl px-3 py-2 flex justify-between items-center gap-2 border border-amber-100">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            #{t.code} {t.title}
                          </p>
                          <p className="text-xs text-gray-400">
                            Deadline:{' '}
                            {t.deadline
                              ? format(parse(t.deadline, 'yyyy-MM-dd', new Date()), 'd MMMM yyyy', { locale: tr })
                              : '—'}
                          </p>
                        </div>
                        <button
                          type="button"
                          className="shrink-0 px-3 py-1.5 bg-amber-500 text-white text-xs font-semibold rounded-lg"
                          onClick={() => setDelegateTask(t)}
                        >
                          Delege Et
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <h2 className="text-sm font-semibold text-gray-700">İzin kayıtlarım</h2>
              {leaves.length === 0 && (
                <p className="text-sm text-gray-400">Henüz izin kaydı yok.</p>
              )}
              {leaves.map((lv) => (
                <div key={lv.id} className="card flex justify-between gap-2 items-start">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {format(parse(lv.start_date, 'yyyy-MM-dd', new Date()), 'd MMMM yyyy', { locale: tr })}
                      {' — '}
                      {format(parse(lv.end_date, 'yyyy-MM-dd', new Date()), 'd MMMM yyyy', { locale: tr })}
                    </p>
                    {lv.note && <p className="text-xs text-gray-500 mt-1">{lv.note}</p>}
                  </div>
                  <button
                    type="button"
                    className="text-xs text-red-600 font-medium shrink-0"
                    onClick={() => removeLeave(lv.id)}
                  >
                    Sil
                  </button>
                </div>
              ))}

              <h2 className="text-sm font-semibold text-gray-700 pt-2">İzin ekle</h2>
              <form onSubmit={submitLeave} className="card space-y-3">
                {formError && <p className="text-sm text-red-600">{formError}</p>}
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Başlangıç</label>
                  <input
                    type="date"
                    className="input"
                    value={form.start_date}
                    onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Bitiş</label>
                  <input
                    type="date"
                    className="input"
                    value={form.end_date}
                    onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Not</label>
                  <textarea
                    className="input min-h-[72px]"
                    value={form.note}
                    onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                    rows={2}
                  />
                </div>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Kaydediliyor…' : 'İzin Ekle'}
                </button>
              </form>
            </div>
          )}
        </>
      )}

      {tab === 'team' && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              className="text-sm text-brand-600 font-medium"
              onClick={() => setMonthCursor((d) => addMonths(d, -1))}
            >
              ‹ Önceki
            </button>
            <h2 className="text-base font-bold text-gray-900">
              {format(monthCursor, 'LLLL yyyy', { locale: tr })}
            </h2>
            <button
              type="button"
              className="text-sm text-brand-600 font-medium"
              onClick={() => setMonthCursor((d) => addMonths(d, 1))}
            >
              Sonraki ›
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-gray-400 mb-1">
            {['Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct', 'Pz'].map((d) => (
              <div key={d}>{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {monthDays.map((day) => {
              const key = format(day, 'yyyy-MM-dd');
              const dayOfWeek = day.getDay(); // 0=Pazar, 6=Cumartesi
              const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
              const muted = !isSameMonth(day, monthCursor);
              const dayLeaves = isWeekend ? [] : (leavesByDay.get(key) || []);
              return (
                <div
                  key={key}
                  className={`min-h-[72px] rounded-lg border p-1 text-left ${
                    isWeekend
                      ? muted ? 'bg-amber-50 border-amber-100 opacity-40' : 'bg-amber-50 border-amber-200'
                      : muted ? 'bg-gray-50 border-gray-100 opacity-50' : 'bg-white border-gray-100'
                  }`}
                >
                  <span className={`text-[10px] ${isWeekend ? 'text-amber-500 font-medium' : 'text-gray-500'}`}>
                    {format(day, 'd')}
                  </span>
                  <div className="flex flex-col gap-0.5 mt-0.5">
                    {dayLeaves.slice(0, 3).map((lv) => (
                      <span
                        key={`${lv.user_id}-${lv.start_date}`}
                        className={`h-1.5 rounded-full ${colorForUserId(lv.user_id)}`}
                        title={`${lv.user_name}: ${format(parse(lv.start_date, 'yyyy-MM-dd', new Date()), 'd MMM', { locale: tr })} – ${format(
                          parse(lv.end_date, 'yyyy-MM-dd', new Date()),
                          'd MMM yyyy',
                          { locale: tr }
                        )}`}
                      />
                    ))}
                    {dayLeaves.length > 3 && (
                      <span className="text-[8px] text-gray-400">+{dayLeaves.length - 3}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Renk Açıklaması */}
          {calendarRows.length > 0 && (() => {
            const seen = new Map();
            for (const row of calendarRows) {
              if (!seen.has(row.user_id)) seen.set(row.user_id, row.user_name);
            }
            return (
              <div className="mt-4 bg-white border border-gray-100 rounded-xl p-3">
                <p className="text-xs font-semibold text-gray-500 mb-2">Renk Açıklaması</p>
                <div className="flex flex-col gap-1.5">
                  {[...seen.entries()].map(([userId, userName]) => (
                    <div key={userId} className="flex items-center gap-2">
                      <span className={`w-4 h-2 rounded-full shrink-0 ${colorForUserId(userId)}`} />
                      <span className="text-xs text-gray-700">{userName}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {delegateTask && (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 sm:items-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-4 space-y-3">
            <h3 className="font-semibold text-gray-900">Delege et</h3>
            <p className="text-sm text-gray-600">
              #{delegateTask.code} — {delegateTask.title}
            </p>
            <select
              className="input"
              value={delegateUserId}
              onChange={(e) => setDelegateUserId(e.target.value)}
            >
              <option value="">Kullanıcı seçin</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name || u.username}
                </option>
              ))}
            </select>
            <div className="flex gap-2">
              <button type="button" className="btn-secondary" onClick={() => setDelegateTask(null)}>
                Vazgeç
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={!delegateUserId || delegateBusy}
                onClick={submitDelegate}
              >
                {delegateBusy ? '…' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
